import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { Language } from 'src/app/modules/core/languages/language.model';
import { AuthToken } from '../../authentication/store/auth';
import { ProfilePreferences } from './notification-prefs.model';
import * as fromPrefs from './notification-prefs.actions';

export const notificationPrefsModuleKey = 'notificationPrefs';
export const notificationPrefsFeatureKey = 'notificationPrefs';

export interface NotificationPrefsModuleState {
  notificationPrefs: NotificationPrefsState;
}

export interface NotificationPrefsState {
  loginLanguages?: string[];
  languages?: Language[];
  showEnterCode: boolean;
  token: AuthToken;
  profilePreferences: ProfilePreferences;
  emailField: string;
  email: string;
  phone: string;
  phoneField: string;
  whatsAppField: string;
  showRegisterEmail: boolean;
  showRegisterPhone: boolean;
  showRegisterWhatsApp: boolean;
  showLoginChoice: boolean;
  showLoginEmail: boolean;
  showLoginPhone: boolean;
  showOptInSmsPhone: boolean;
  showVerifyCodeEmail: boolean;
  showVerifyCodePhone: boolean;
  err: any;
  isLoading: boolean;
}

export const initialNotificationPrefsState: NotificationPrefsState = {
  loginLanguages: [],
  languages: [],
  showEnterCode: false,
  token: null,
  profilePreferences: null,
  emailField: null,
  email: null,
  phone: null,
  phoneField: null,
  whatsAppField: null,
  showRegisterEmail: false,
  showRegisterPhone: false,
  showRegisterWhatsApp: false,
  showLoginChoice: true,
  showLoginEmail: false,
  showLoginPhone: false,
  showOptInSmsPhone: false,
  showVerifyCodeEmail: false,
  showVerifyCodePhone: false,
  err: null,
  isLoading: false
};

export const reducer: ActionReducer<NotificationPrefsState, Action> = createReducer(
  initialNotificationPrefsState,
  on(fromPrefs.showLoginChoice, (state, action) => {
    return {
      ...state,
      showLoginChoice: true,
      showLoginEmail: false,
      showLoginPhone: false,
      showOptInSmsPhone: false,
      showVerifyCodeEmail: false,
      showVerifyCodePhone: false
    };
  }),
  on(fromPrefs.showLoginEmail, (state, action) => {
    return {
      ...state,
      showLoginChoice: false,
      showLoginEmail: true,
      showLoginPhone: false,
      showOptInSmsPhone: false,
      showVerifyCodeEmail: false,
      showVerifyCodePhone: false
    };
  }),
  on(fromPrefs.showLoginPhone, (state, action) => {
    return {
      ...state,
      showLoginChoice: false,
      showLoginEmail: false,
      showLoginPhone: true,
      showOptInSmsPhone: false,
      showVerifyCodeEmail: false,
      showVerifyCodePhone: false
    };
  }),
  on(fromPrefs.showOptInSmsPhone, (state, action) => {
    return {
      ...state,
      phone: action.phone,
      showLoginChoice: false,
      showLoginEmail: false,
      showLoginPhone: false,
      showOptInSmsPhone: true,
      showVerifyCodeEmail: false,
      showVerifyCodePhone: false
    };
  }),
  on(fromPrefs.showVerifyCodeEmail, (state, action) => {
    return {
      ...state,
      showLoginChoice: false,
      showLoginEmail: false,
      showLoginPhone: false,
      showOptInSmsPhone: false,
      showVerifyCodeEmail: true,
      showVerifyCodePhone: false
    };
  }),
  on(fromPrefs.showVerifyCodePhone, (state, action) => {
    return {
      ...state,
      showLoginChoice: false,
      showLoginEmail: false,
      showLoginPhone: false,
      showOptInSmsPhone: false,
      showVerifyCodeEmail: false,
      showVerifyCodePhone: true
    };
  }),
  on(fromPrefs.loadAvailableLanguagesForLoginComplete, (state, action) => {
    return {
      ...state,
      loginLanguages: action.languages
    };
  }),
  on(fromPrefs.loadAvailableLanguagesComplete, (state, action) => {
    return {
      ...state,
      languages: action.languages
    };
  }),
  on(fromPrefs.sendOneTimeAuthWhatsAppComplete, (state, action) => {
    return {
      ...state,
      showEnterCode: true,
      err: null
    };
  }),
  on(fromPrefs.cancelEnterCode, (state, action) => {
    return {
      ...state,
      showEnterCode: false,
      err: null
    };
  }),
  on(fromPrefs.getNotificationPreferencesComplete, (state, action) => {
    return {
      ...state,
      profilePreferences: action.preferences
    };
  }),
  on(fromPrefs.updateNotificationPreferencesComplete, (state, action) => {
    return {
      ...state,
      profilePreferences: action.preferences
    };
  }),
  on(fromPrefs.processLoginAuthCodeForEmailComplete, (state, action) => {
    return {
      ...state,
      token: action.authToken,
      showLoginChoice: false,
      showLoginEmail: false,
      showLoginPhone: false,
      showOptInSmsPhone: false,
      showVerifyCodeEmail: false,
      showVerifyCodePhone: false
    };
  }),
  on(fromPrefs.processLoginAuthCodeForPhoneComplete, (state, action) => {
    return {
      ...state,
      token: action.authToken,
      showLoginChoice: false,
      showLoginEmail: false,
      showLoginPhone: false,
      showOptInSmsPhone: false,
      showVerifyCodeEmail: false,
      showVerifyCodePhone: false
    };
  }),
  on(fromPrefs.processAuthCodeForEmailComplete, (state, action) => {
    return {
      ...state,
      profilePreferences: action.preferences,
      showEnterCode: false,
      emailField: null,
      showRegisterEmail: false,
      phoneField: null,
      showRegisterPhone: false,
      whatsAppField: null,
      showRegisterWhatsApp: false
    };
  }),
  on(fromPrefs.processAuthCodeForPhoneComplete, (state, action) => {
    return {
      ...state,
      profilePreferences: action.preferences,
      showEnterCode: false,
      emailField: null,
      showRegisterEmail: false,
      phoneField: null,
      showRegisterPhone: false,
      whatsAppField: null,
      showRegisterWhatsApp: false
    };
  }),
  on(fromPrefs.processAuthCodeForWhatsAppComplete, (state, action) => {
    return {
      ...state,
      profilePreferences: action.preferences,
      showEnterCode: false,
      emailField: null,
      showRegisterEmail: false,
      phoneField: null,
      showRegisterPhone: false,
      whatsAppField: null,
      showRegisterWhatsApp: false
    };
  }),
  on(fromPrefs.setError, (state, action) => {
    return {
      ...state,
      err: action.err,
      isLoading: false
    };
  }),
  on(fromPrefs.changeEmailField, (state, action) => {
    return {
      ...state,
      err: null,
      emailField: action.emailField,
      showRegisterEmail: true,
      phoneField: null,
      showRegisterPhone: false,
      whatsAppField: null,
      showRegisterWhatsApp: false
    };
  }),
  on(fromPrefs.changePhoneField, (state, action) => {
    return {
      ...state,
      err: null,
      emailField: null,
      showRegisterEmail: false,
      phoneField: action.phoneField,
      showRegisterPhone: true,
      whatsAppField: null,
      showRegisterWhatsApp: false
    };
  }),
  on(fromPrefs.changeWhatsAppField, (state, action) => {
    return {
      ...state,
      err: null,
      emailField: null,
      showRegisterEmail: false,
      phoneField: null,
      showRegisterPhone: false,
      whatsAppField: action.whatsAppField,
      showRegisterWhatsApp: true
    };
  }),
  on(fromPrefs.cancelChangeEmailField, (state, action) => {
    return {
      ...state,
      err: null,
      emailField: null,
      showRegisterEmail: false,
      phoneField: null,
      showRegisterPhone: false,
      whatsAppField: null,
      showRegisterWhatsApp: false
    };
  }),
  on(fromPrefs.cancelChangePhoneField, (state, action) => {
    return {
      ...state,
      err: null,
      emailField: null,
      showRegisterEmail: false,
      phoneField: null,
      showRegisterPhone: false,
      whatsAppField: null,
      showRegisterWhatsApp: false
    };
  }),
  on(fromPrefs.cancelChangeWhatsAppField, (state, action) => {
    return {
      ...state,
      err: null,
      emailField: null,
      showRegisterEmail: false,
      phoneField: null,
      showRegisterPhone: false,
      whatsAppField: null,
      showRegisterWhatsApp: false
    };
  }),
  on(fromPrefs.sendOnTimeAuthEmailComplete, (state, action) => {
    return {
      ...state,
      email: action.email,
      showEnterCode: true
    };
  }),
  on(fromPrefs.sendOnTimeAuthPhoneComplete, (state, action) => {
    return {
      ...state,
      phone: action.phone,
      showEnterCode: true
    };
  }),
  on(fromPrefs.logoutPreferencesComplete, (state, action) => {
    return { ...initialNotificationPrefsState, err: null };
  }),
  on(
    fromPrefs.sendOneTimeAuthPhone,
    fromPrefs.sendOneTimeAuthWhatsApp,
    fromPrefs.sendOneTimeAuthEmail,
    fromPrefs.processAuthCodeForPhone,
    fromPrefs.processAuthCodeForEmail,
    fromPrefs.processAuthCodeForWhatsApp,
    state => ({
      ...state,
      isLoading: true
    })
  ),
  on(
    fromPrefs.sendOnTimeAuthPhoneComplete,
    fromPrefs.sendOnTimeAuthEmailComplete,
    fromPrefs.sendOneTimeAuthWhatsAppComplete,
    fromPrefs.processAuthCodeForPhoneComplete,
    fromPrefs.processAuthCodeForEmailComplete,
    fromPrefs.processAuthCodeForWhatsAppComplete,
    state => ({
      ...state,
      isLoading: false
    })
  )
);

export const getLoginLanguages = (state: NotificationPrefsState) => state?.loginLanguages;

export const getLanguages = (state: NotificationPrefsState) => state?.languages;

export const getShowEnterCode = (state: NotificationPrefsState) => state?.showEnterCode;

export const getToken = (state: NotificationPrefsState) => state?.token;

export const getError = (state: NotificationPrefsState) => state?.err;

export const getPreferences = (state: NotificationPrefsState) => state?.profilePreferences;

export const getShowRegisterEmail = (state: NotificationPrefsState) => state?.showRegisterEmail;

export const getEmailField = (state: NotificationPrefsState) => state?.emailField;

export const getShowRegisterPhone = (state: NotificationPrefsState) => state?.showRegisterPhone;

export const getPhoneField = (state: NotificationPrefsState) => state?.phoneField;

export const getShowRegisterWhatsApp = (state: NotificationPrefsState) => state?.showRegisterWhatsApp;

export const getWhatsAppField = (state: NotificationPrefsState) => state?.whatsAppField;

export const getShowLoginChoice = (state: NotificationPrefsState) => state?.showLoginChoice;

export const getShowLoginEmail = (state: NotificationPrefsState) => state?.showLoginEmail;

export const getShowLoginPhone = (state: NotificationPrefsState) => state?.showLoginPhone;

export const getShowOptInSmsPhone = (state: NotificationPrefsState) => state?.showOptInSmsPhone;

export const getShowVerifyCodeEmail = (state: NotificationPrefsState) => state?.showVerifyCodeEmail;

export const getShowVerifyCodePhone = (state: NotificationPrefsState) => state?.showVerifyCodePhone;

export const getEmail = (state: NotificationPrefsState) => state?.email;

export const getPhone = (state: NotificationPrefsState) => state?.phone;

export const getIsLoading = (state: NotificationPrefsState) => state.isLoading;
